/**
 * A content status enumeration for moderation workflow
 */
export type ContentStatus="pending"|"approved"|"denied";

export type UserRole="admin"|"guest";

/**
 * An account (tenant) within the system
 * @table account
 * @schema public
 */
export interface Account
{
    /**
     * Unique id of the account
     */
    id:string;
    /**
     * When the account was created
     */
    created_at:string;
    /**
     * Display name of the account
     */
    name:string;
    /**
     * Path to the account's logo image in the 'accounts' storage bucket
     */
    logo_image_path?:string;
    /**
     * Path to the account's hero image in the 'accounts' storage bucket
     */
    hero_image_path?:string;
    is_default:boolean;
}

/**
 * @insertFor Account
 * @table account
 * @schema public
 */
export interface Account_insert
{
    id?:string;
    created_at?:string;
    name:string;
    logo_image_path?:string;
    hero_image_path?:string;
    is_default?:boolean;
}

/**
 * An invitation to join an account (used for accept-invite flow)
 * @table account_invite
 * @schema public
 */
export interface AccountInvite
{
    /**
     * Unique id of the account invite
     */
    id:string;
    /**
     * When the invite was created
     */
    created_at:string;
    /**
     * The account the invite belongs to
     */
    account_id:string;
    /**
     * The email address being invited
     */
    invited_email:string;
    /**
     * Proposed role of the invited user within the account
     */
    role:string;
    /**
     * Unique invite code used to accept or decline the invite
     */
    invite_code:string;
    /**
     * The user who created the invite
     */
    invited_by_user_id?:string;
    /**
     * When this invite expires (if set)
     */
    expires_at?:string;
    /**
     * When the invite was accepted (if accepted)
     */
    accepted_at?:string;
    /**
     * The user who accepted the invite (if accepted)
     */
    accepted_by_user_id?:string;
}

/**
 * @insertFor AccountInvite
 * @table account_invite
 * @schema public
 */
export interface AccountInvite_insert
{
    id?:string;
    created_at?:string;
    account_id:string;
    invited_email:string;
    role?:string;
    invite_code:string;
    invited_by_user_id?:string;
    expires_at?:string;
    accepted_at?:string;
    accepted_by_user_id?:string;
}

/**
 * Links a user to an account with a role
 * @table account_membership
 * @schema public
 */
export interface AccountMembership
{
    /**
     * Unique id of the account membership
     */
    id:string;
    /**
     * When the membership was created
     */
    created_at:string;
    /**
     * The last time the user accessed this account
     */
    last_accessed_at:string;
    /**
     * The user who is a member of the account
     */
    user_id:string;
    /**
     * The account the user belongs to
     */
    account_id:string;
    /**
     * The role of the user within the account
     */
    role:string;
}

/**
 * @insertFor AccountMembership
 * @table account_membership
 * @schema public
 */
export interface AccountMembership_insert
{
    id?:string;
    created_at?:string;
    last_accessed_at?:string;
    user_id:string;
    account_id:string;
    role?:string;
}

/**
 * @table form_submission
 * @schema public
 */
export interface FormSubmission
{
    id:string;
    created_at:string;
    data:Record<string,any>;
    name:string;
}

/**
 * @insertFor FormSubmission
 * @table form_submission
 * @schema public
 */
export interface FormSubmission_insert
{
    id?:string;
    created_at?:string;
    data:Record<string,any>;
    name?:string;
}

/**
 * A person (content-creating Guardian) who leads or represents initiatives
 * @table guardian
 * @schema public
 */
export interface Guardian
{
    /**
     * Unique id of the guardian
     */
    id:string;
    /**
     * When the guardian was created
     */
    created_at:string;
    /**
     * Owning account of this guardian
     */
    account_id:string;
    /**
     * The user who created this guardian (if available)
     */
    created_by_user_id?:string;
    /**
     * Display name of the guardian
     */
    name:string;
    /**
     * Moderation status of the guardian
     */
    status:string;
    /**
     * Short description of the guardian
     */
    description?:string;
    /**
     * Long-form markdown description of the guardian
     */
    long_description?:string;
    /**
     * Primary image path for the guardian
     */
    image_path?:string;
    /**
     * Optional video path for the guardian
     */
    video_path?:string;
    /**
     * Tags used for discovery and related content
     */
    tags:string[];
    /**
     * Social links (URLs) associated with the guardian
     */
    social_links:string[];
    /**
     * Additional image paths for media gallery
     */
    image_paths:string[];
    /**
     * Whether the guardian is visible across accounts
     */
    is_public:boolean;
    /**
     * Whether the guardian should be featured on the landing page
     */
    is_featured:boolean;
    /**
     * what they love
     */
    what_they_love?:string;
}

/**
 * @insertFor Guardian
 * @table guardian
 * @schema public
 */
export interface Guardian_insert
{
    id?:string;
    created_at?:string;
    account_id:string;
    created_by_user_id?:string;
    name:string;
    status?:string;
    description?:string;
    long_description?:string;
    image_path?:string;
    video_path?:string;
    tags?:string[];
    social_links?:string[];
    image_paths?:string[];
    is_public?:boolean;
    is_featured?:boolean;
    what_they_love?:string;
}

/**
 * A project (initiative) associated with a guardian
 * @table project
 * @schema public
 */
export interface Project
{
    /**
     * Unique id of the project
     */
    id:string;
    /**
     * When the project was created
     */
    created_at:string;
    /**
     * Owning account of this project
     */
    account_id:string;
    /**
     * The user who created this project (if available)
     */
    created_by_user_id?:string;
    /**
     * The guardian who owns this project
     */
    guardian_id:string;
    /**
     * Display name of the project
     */
    name:string;
    /**
     * Moderation status of the project
     */
    status:string;
    /**
     * Short slogan/value statement of the project
     */
    slogan?:string;
    /**
     * Short description of the project
     */
    description?:string;
    /**
     * Long-form markdown description of the project
     */
    long_description?:string;
    /**
     * Tags used for discovery and related content
     */
    tags:string[];
    /**
     * Latitude of the project's location
     */
    lat?:number;
    /**
     * Longitude of the project's location
     */
    lng?:number;
    /**
     * Primary image path for the project
     */
    image_path?:string;
    /**
     * Optional video path for the project
     */
    video_path?:string;
    /**
     * Social links (URLs) associated with the project
     */
    social_links:string[];
    /**
     * Project website URL
     */
    website_url?:string;
    /**
     * Project needs (actionable items)
     */
    needs:string[];
    /**
     * Additional image paths for media gallery
     */
    image_paths:string[];
    /**
     * Whether the project is visible across accounts
     */
    is_public:boolean;
    /**
     * Whether the project should be featured on the landing page
     */
    is_featured:boolean;
}

/**
 * @insertFor Project
 * @table project
 * @schema public
 */
export interface Project_insert
{
    id?:string;
    created_at?:string;
    account_id:string;
    created_by_user_id?:string;
    guardian_id:string;
    name:string;
    status?:string;
    slogan?:string;
    description?:string;
    long_description?:string;
    tags?:string[];
    lat?:number;
    lng?:number;
    image_path?:string;
    video_path?:string;
    social_links?:string[];
    website_url?:string;
    needs?:string[];
    image_paths?:string[];
    is_public?:boolean;
    is_featured?:boolean;
}

/**
 * A user
 * @table user
 * @schema public
 */
export interface User
{
    /**
     * Unique id of the user
     */
    id:string;
    /**
     * When the user was created
     */
    created_at:string;
    /**
     * Full name of the user
     */
    name:string;
    /**
     * Email address of the user
     */
    email:string;
    /**
     * Path to the user's profile image in the 'accounts' storage bucket
     */
    profile_image_path?:string;
    /**
     * Path to the user's hero image in the 'accounts' storage bucket
     */
    hero_image_path?:string;
}

/**
 * @insertFor User
 * @table user
 * @schema public
 */
export interface User_insert
{
    id?:string;
    created_at?:string;
    name:string;
    email:string;
    profile_image_path?:string;
    hero_image_path?:string;
}